import { useEffect, useState } from "react";
import { Store, IndianRupee, ChevronDown, History, Clock, Package, Clapperboard } from "lucide-react";
import { WEEKDAYS, DEFAULT_WORKING_DAYS } from "../../lib/enums";
import ClockTimePicker from "../../components/ClockTimePicker";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select } from "../../components/ui";
import { createEmployee, updateEmployee, listEmployees } from "../../api/employees";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import { readCurrentSalary, formatMoney } from "../../lib/salary";
import { formatShiftRange } from "../../lib/records";

// Employees — API docs §4. Both tiers manage staff here, at different scope:
//
//   BRANCH  → only its own staff. `partner` is derived from the token and must
//             never be sent.
//   CHAIN   → staff across all of its branches, plus its own head-office staff.
//             `partner` (a branch id) is sent on create to say which branch the
//             person belongs to; omitting it attaches them to the chain itself.
//
// `reportingTo` managers must belong to the same branch as the employee, so the
// manager picker is always scoped to the branch selected above it.
const emptyForm = {
  name: "",
  phone: "",
  designation: "",
  reportingTo: [],
  status: "ACTIVE",
  partner: "", // "" = head office (chain login only)
  salary: { monthlyAmount: "", effectiveFrom: "", note: "" },
  // §12 work profile. Empty timings mean "no profile" — the employee keeps
  // the simple behaviour where only recorded days count.
  shift: { startTime: "", endTime: "", workingDays: [], graceMinutes: "" },
  // §4.1 / §4.5 — permissions is REPLACED not merged; always send the full
  // desired set. [] revokes everything.
  permissions: [],
};

export default function EmployeeFormModal({
  open,
  onClose,
  employee,
  onSaved,
  defaultBranchId = "",
  // Current pay, when the caller has already loaded the payroll record. Salary
  // lives in its own collection, so the employee object often won't carry it.
  currentSalary: currentSalaryProp = null,
}) {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [form, setForm] = useState(emptyForm);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [showSalary, setShowSalary] = useState(false);
  const [showShift, setShowShift] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showCinemaPerms, setShowCinemaPerms] = useState(false);

  const isEdit = !!employee;
  // What this person is paid today, shown so a revision is entered against a
  // visible baseline. Prefer the figure the caller already fetched from
  // payroll; otherwise try to read one off the employee record. Hidden when
  // neither is available.
  const currentSalary = currentSalaryProp ?? readCurrentSalary(employee);

  // The branch this employee sits in. For a branch login it is implicit (the
  // token decides); for a chain it is whatever the selector says.
  const scopeBranchId = isChain ? form.partner : "";

  useEffect(() => {
    if (!open) return;
    setForm(
      employee
        ? {
            name: employee.name ?? "",
            phone: employee.phone ?? "",
            designation: employee.designation ?? "",
            reportingTo: (employee.reportingTo ?? []).map((m) => m?._id ?? m),
            status: employee.status ?? "ACTIVE",
            partner: employee.partner?._id ?? employee.partner ?? "",
            salary: { monthlyAmount: "", effectiveFrom: "", note: "" },
            shift: {
              startTime: employee.shift?.startTime ?? "",
              endTime: employee.shift?.endTime ?? "",
              workingDays: employee.shift?.workingDays ?? [],
              graceMinutes: employee.shift?.graceMinutes ?? "",
            },
            permissions: Array.isArray(employee.permissions) ? employee.permissions : [],
          }
        : { ...emptyForm, partner: defaultBranchId }
    );
    setErrors({});
    setFormError("");
    setShowSalary(false);
    setShowShift(false);
    setShowPermissions(false);
  }, [open, employee, defaultBranchId]);

  // A chain needs its branch list to target the new hire.
  useEffect(() => {
    if (!open || !isChain) return;
    listBranches({ limit: 100, status: "ACTIVE" })
      .then((res) => setBranches(res.data?.items || []))
      .catch(() => setBranches([]));
  }, [open, isChain]);

  // Managers are re-fetched whenever the branch changes — a manager from a
  // different branch would be rejected by the API.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setManagersLoading(true);
    listEmployees({
      limit: 100,
      status: "ACTIVE",
      ...(isChain && scopeBranchId ? { partner: scopeBranchId } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.items || []).filter((e) => e._id !== employee?._id);
        // A chain asking for "head office" gets every branch's staff back from
        // the unscoped list endpoint, so narrow it to the chain's own records.
        setManagers(
          isChain && !scopeBranchId
            ? list.filter((e) => !e.partner || e.partner === partner?._id)
            : list
        );
      })
      .catch(() => !cancelled && setManagers([]))
      .finally(() => !cancelled && setManagersLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, scopeBranchId, isChain, employee?._id, partner?._id]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function updateSalary(field, value) {
    setForm((f) => ({ ...f, salary: { ...f.salary, [field]: value } }));
  }

  function updateShift(field, value) {
    setForm((f) => ({ ...f, shift: { ...f.shift, [field]: value } }));
  }

  function toggleWorkingDay(day) {
    setForm((f) => {
      const days = f.shift.workingDays.includes(day)
        ? f.shift.workingDays.filter((d) => d !== day)
        : [...f.shift.workingDays, day].sort((a, b) => a - b);
      return { ...f, shift: { ...f.shift, workingDays: days } };
    });
  }

  // The one salary payload both paths use — §4.1's shape. Returns undefined
  // when no amount was entered, which is what "leave pay alone" looks like.
  function buildSalary() {
    const amount = String(form.salary.monthlyAmount).trim();
    if (amount === "") return undefined;
    return {
      monthlyAmount: Number(amount),
      ...(form.salary.effectiveFrom ? { effectiveFrom: form.salary.effectiveFrom } : {}),
      ...(form.salary.note.trim() ? { note: form.salary.note.trim() } : {}),
    };
  }

  // §12 — the work profile. Timings are "set together or not at all", and the
  // server merges rather than replaces, so only what was filled in is sent.
  // Grace / full-day / half-day are derived from the shift length when omitted,
  // so they are only included when explicitly overridden.
  function buildShift() {
    const { startTime, endTime, workingDays, graceMinutes } = form.shift;
    const hasTimings = !!(startTime && endTime);
    const hasDays = workingDays.length > 0;
    const hasGrace = String(graceMinutes).trim() !== "";
    if (!hasTimings && !hasDays && !hasGrace) return undefined;
    return {
      ...(hasTimings ? { startTime, endTime } : {}),
      ...(hasDays ? { workingDays } : {}),
      ...(hasGrace ? { graceMinutes: Number(graceMinutes) } : {}),
    };
  }

  // Changing branch invalidates any manager already picked from the old one.
  function changeBranch(value) {
    setForm((f) => ({ ...f, partner: value, reportingTo: [] }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        designation: form.designation.trim() || undefined,
        reportingTo: form.reportingTo,
      };

      const salary = buildSalary();
      const shift = buildShift();

      // §4.1 / §4.5 — both the create and the update body take `shift` and
      // `salary` directly, so a work profile never needs a second call.
      // `shift` is merged server-side, so sending only what changed is enough.
      if (shift) payload.shift = shift;
      if (salary) payload.salary = salary;
      // permissions is always sent — [] revokes everything, a non-empty array
      // replaces the current set. The docs explicitly say it is never merged.
      payload.permissions = form.permissions;

      if (isEdit) {
        payload.status = form.status;
        await updateEmployee(employee._id, payload);
      } else {
        // Chain only: which branch this person belongs to. Omitted entirely for
        // head-office staff, and never sent by a branch login.
        if (isChain && form.partner) payload.partner = form.partner;
        await createEmployee(payload);
      }
      onSaved();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors?.length) {
        const map = {};
        apiErrors.forEach((e) => (map[e.path] = e.message));
        setErrors(map);
      } else {
        setFormError(err?.response?.data?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  function toggleManager(id) {
    setForm((f) => ({
      ...f,
      reportingTo: f.reportingTo.includes(id)
        ? f.reportingTo.filter((m) => m !== id)
        : [...f.reportingTo, id],
    }));
  }

  const branchLabel =
    branches.find((b) => b._id === form.partner)?.name ||
    (form.partner ? "this branch" : isChain ? "head office" : "your branch");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit employee" : "Add employee"}
      description={
        isEdit
          ? "Updating " + (employee?.name ?? "this employee") + "'s record."
          : "Add a new member to your staff directory."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        {/* Branch targeting — chain logins only. On edit the branch is fixed:
            moving staff between branches is not part of the update contract. */}
        {isChain && (
          <Field
            label="Branch"
            error={errors.partner}
            hint={
              isEdit
                ? "An employee's branch cannot be changed after they are created."
                : "Which outlet this person works at. Leave as head office for chain-level staff."
            }
          >
            {isEdit ? (
              <div className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-700">
                <Store size={15} className="text-gray-400" />
                {employee?.partner?.name || (form.partner ? "Branch" : "Head office")}
              </div>
            ) : (
              <Select
                value={form.partner}
                onChange={(e) => changeBranch(e.target.value)}
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">Head office (chain)</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Full name" required error={errors.name}>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Asha Rao"
                required
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </Field>
          </div>

          <Field
            label="Phone number"
            required
            error={errors.phone}
            hint="10-digit Indian mobile — unique within your organization"
          >
            <Input
              value={form.phone}
              onChange={(e) => update("phone", e.target.value.replace(/\D/g, ""))}
              placeholder="9812345678"
              inputMode="numeric"
              maxLength={10}
              required
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>

          <Field label="Designation" error={errors.designation}>
            <Input
              value={form.designation}
              onChange={(e) => update("designation", e.target.value)}
              placeholder="Floor Supervisor"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        {isEdit && (
          <Field label="Status" error={errors.status}>
            <Select
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
          </Field>
        )}

        <Field
          label="Reports to"
          error={errors.reportingTo}
          hint={"Managers must work at the same location — showing staff at " + branchLabel + "."}
        >
          <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-gray-200 p-2">
            {managersLoading ? (
              <p className="px-1 py-2 text-sm text-gray-500">Loading colleagues…</p>
            ) : managers.length === 0 ? (
              <p className="px-1 py-2 text-sm text-gray-500">
                No other employees at {branchLabel} yet.
              </p>
            ) : (
              managers.map((m) => (
                <label
                  key={m._id}
                  className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-emerald-50 transition-colors duration-200"
                >
                  <input
                    type="checkbox"
                    checked={form.reportingTo.includes(m._id)}
                    onChange={() => toggleManager(m._id)}
                    className="h-4 w-4 accent-emerald-500"
                  />
                  <span className="text-sm text-gray-700">
                    {m.name}
                    {m.designation && <span className="text-gray-500"> — {m.designation}</span>}
                  </span>
                </label>
              ))
            )}
          </div>
        </Field>

        {/* Work profile — §12. Optional, and genuinely consequential: without
            it only recorded days count toward pay, so an employee who worked 26
            of 31 days is paid 26/31. With working days known, their week-offs
            become payable too and the same month pays 30/31. */}
        <div className="rounded-md border border-emerald-100 bg-emerald-50/40">
          <button
            type="button"
            onClick={() => setShowShift((s) => !s)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Clock size={15} className="text-emerald-600" />
              Shift &amp; working days
              <span className="text-xs font-normal text-gray-500">(optional)</span>
            </span>
            <div className="flex items-center gap-2">
              {form.shift.startTime && form.shift.endTime && (
                <span className="text-xs font-semibold text-emerald-700">
                  {formatShiftRange(form.shift.startTime, form.shift.endTime)}
                </span>
              )}
              <ChevronDown
                size={16}
                className={"text-gray-400 transition-transform " + (showShift ? "rotate-180" : "")}
              />
            </div>
          </button>

          {showShift && (
            <div className="border-t border-emerald-100 px-3.5 pb-3 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                <Field label="Starts" error={errors["shift.startTime"]}>
                  <ClockTimePicker
                    value={form.shift.startTime}
                    onChange={(v) => updateShift("startTime", v)}
                  />
                </Field>
                <Field label="Ends" error={errors["shift.endTime"]}>
                  <ClockTimePicker
                    value={form.shift.endTime}
                    onChange={(v) => updateShift("endTime", v)}
                  />
                </Field>
                <Field label="Grace (min)" error={errors["shift.graceMinutes"]} hint="Default 15">
                  <Input
                    value={form.shift.graceMinutes}
                    onChange={(e) => updateShift("graceMinutes", e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    placeholder="15"
                    className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  />
                </Field>
              </div>

              {/* An end before the start is a night shift, not an error. */}
              {form.shift.startTime && form.shift.endTime && form.shift.endTime < form.shift.startTime && (
                <p className="-mt-2 mb-3 text-xs text-gray-500">
                  Ends before it starts — treated as a night shift running past midnight.
                </p>
              )}

              <span className="mb-1.5 block text-label-md text-on-surface">Working days</span>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day) => {
                  const on = form.shift.workingDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleWorkingDay(day.value)}
                      className={`h-9 w-11 rounded-md text-xs font-semibold transition-colors ${
                        on
                          ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm"
                          : "bg-white text-gray-500 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-700"
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              {errors["shift.workingDays"] && (
                <span className="mt-1 block text-body-sm text-error">{errors["shift.workingDays"]}</span>
              )}
              <p className="mt-2 text-xs text-gray-500">
                {form.shift.workingDays.length === 0 ? (
                  <>
                    None picked — the API defaults to Mon–Sat when timings are set.{" "}
                    <button
                      type="button"
                      onClick={() => updateShift("workingDays", DEFAULT_WORKING_DAYS)}
                      className="font-semibold text-emerald-700 underline"
                    >
                      Use Mon–Sat
                    </button>
                  </>
                ) : (
                  <>
                    Days off count as payable week-offs; a missed working day counts as an absence.
                    Today is never counted absent.
                  </>
                )}
              </p>
              {isEdit && (
                <p className="mt-2 text-xs text-gray-500">
                  Changes merge into the existing shift — blank fields are left alone.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Inventory access — §4.1 / §4.5. permissions is REPLACED not merged.
            Always send the full desired set; [] revokes all access. */}
        <div className="rounded-md border border-emerald-100 bg-emerald-50/40">
          <button
            type="button"
            onClick={() => setShowPermissions((s) => !s)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package size={15} className="text-emerald-600" />
              Inventory access
            </span>
            <div className="flex items-center gap-2">
              {form.permissions.length > 0 && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                  {form.permissions.length} granted
                </span>
              )}
              <ChevronDown
                size={16}
                className={"text-gray-400 transition-transform " + (showPermissions ? "rotate-180" : "")}
              />
            </div>
          </button>

          {showPermissions && (
            <div className="border-t border-emerald-100 px-3.5 pb-3 pt-3 space-y-2">
              <p className="text-xs text-gray-500 mb-3">
                Controls what this employee can do with inventory in their phone app.
                Changes take effect immediately — no re-login required.
              </p>
              {[
                {
                  key: "inventory.view",
                  label: "View stock",
                  description: "See the catalogue and current stock levels",
                },
                {
                  key: "inventory.record",
                  label: "Record entries",
                  description: "Upload photos and submit receipts, consumption, wastage",
                },
                {
                  key: "inventory.history",
                  label: "View history",
                  description: "Browse past submissions and the movement ledger",
                },
              ].map((perm) => {
                const on = form.permissions.includes(perm.key);
                return (
                  <label
                    key={perm.key}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      on
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-gray-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        update(
                          "permissions",
                          on
                            ? form.permissions.filter((p) => p !== perm.key)
                            : [...form.permissions, perm.key]
                        );
                      }}
                      className="mt-0.5 h-4 w-4 accent-emerald-500 shrink-0"
                    />
                    <div>
                      <p className={`text-sm font-semibold ${on ? "text-emerald-800" : "text-gray-700"}`}>
                        {perm.label}
                      </p>
                      <p className="text-xs text-gray-500">{perm.description}</p>
                    </div>
                  </label>
                );
              })}
              {isEdit && !form.permissions.some(p => p.startsWith("inventory.")) && (
                <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                  No inventory access — this employee cannot see or record stock.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Cinema access — §4.1 / §4.5 (movieKeys.*). Same replace-not-merge rule. */}
        <div className="rounded-md border border-blue-100 bg-blue-50/30">
          <button
            type="button"
            onClick={() => setShowCinemaPerms(s => !s)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Clapperboard size={15} className="text-blue-600" />
              Cinema access
            </span>
            <div className="flex items-center gap-2">
              {form.permissions.some(p => p.startsWith("movieKeys.")) && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                  {form.permissions.filter(p => p.startsWith("movieKeys.")).length} granted
                </span>
              )}
              <ChevronDown
                size={16}
                className={"text-gray-400 transition-transform " + (showCinemaPerms ? "rotate-180" : "")}
              />
            </div>
          </button>

          {showCinemaPerms && (
            <div className="border-t border-blue-100 px-3.5 pb-3 pt-3 space-y-2">
              <p className="text-xs text-gray-500 mb-3">
                Controls what this employee can do with cinema key management in their phone app.
                Changes take effect immediately — no re-login required.
              </p>
              {[
                {
                  key: "movieKeys.view",
                  label: "View keys",
                  description: "See the keys held in their own name",
                },
                {
                  key: "movieKeys.consume",
                  label: "Consume keys",
                  description: "Key off a show — spend a key for a screening",
                },
                {
                  key: "movieKeys.history",
                  label: "View history",
                  description: "Browse their own key movement history",
                },
              ].map((perm) => {
                const on = form.permissions.includes(perm.key);
                return (
                  <label
                    key={perm.key}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      on
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/40"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        update(
                          "permissions",
                          on
                            ? form.permissions.filter(p => p !== perm.key)
                            : [...form.permissions, perm.key]
                        );
                      }}
                      className="mt-0.5 h-4 w-4 accent-blue-500 shrink-0"
                    />
                    <div>
                      <p className={`text-sm font-semibold ${on ? "text-blue-800" : "text-gray-700"}`}>
                        {perm.label}
                      </p>
                      <p className="text-xs text-gray-500">{perm.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Salary. On create this is the opening package; on edit it appends a
            new revision, because §4.1 treats pay as revision history rather
            than a field that gets overwritten. Either way the payload shape is
            identical — { monthlyAmount, effectiveFrom?, note? } — and it rides
            along on the employee body rather than needing a second call. */}
        <div className="rounded-md border border-emerald-100 bg-emerald-50/40">
          <button
            type="button"
            onClick={() => setShowSalary((s) => !s)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <IndianRupee size={15} className="text-emerald-600" />
              {isEdit ? "Revise salary" : "Starting salary"}
              <span className="text-xs font-normal text-gray-500">(optional)</span>
            </span>
            <div className="flex items-center gap-2">
              {isEdit && currentSalary != null && (
                <span className="text-xs font-semibold text-emerald-700">
                  Now {formatMoney(currentSalary)}/mo
                </span>
              )}
              <ChevronDown
                size={16}
                className={"text-gray-400 transition-transform " + (showSalary ? "rotate-180" : "")}
              />
            </div>
          </button>

          {showSalary && (
            <div className="border-t border-emerald-100 px-3.5 pb-1 pt-3">
              {isEdit && (
                <p className="mb-3 flex items-start gap-2 rounded bg-white/70 px-3 py-2 text-xs text-gray-600">
                  <History size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>
                    This records a <strong className="font-semibold">new revision</strong> and keeps
                    the old figure in {employee?.name ?? "this employee"}&apos;s history — it does
                    not overwrite it. Leave blank to change nothing.
                  </span>
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <Field
                  label={isEdit ? "New monthly amount" : "Monthly amount"}
                  error={errors["salary.monthlyAmount"] || errors.monthlyAmount}
                >
                  <Input
                    value={form.salary.monthlyAmount}
                    onChange={(e) =>
                      updateSalary("monthlyAmount", e.target.value.replace(/[^\d.]/g, ""))
                    }
                    placeholder={currentSalary != null ? String(currentSalary) : "25000"}
                    inputMode="decimal"
                    className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  />
                </Field>
                <Field
                  label="Effective from"
                  error={errors["salary.effectiveFrom"] || errors.effectiveFrom}
                >
                  <Input
                    type="date"
                    value={form.salary.effectiveFrom}
                    onChange={(e) => updateSalary("effectiveFrom", e.target.value)}
                    className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  />
                </Field>
              </div>
              <Field label="Note" error={errors["salary.note"] || errors.note}>
                <Input
                  value={form.salary.note}
                  onChange={(e) => updateSalary("note", e.target.value)}
                  placeholder={isEdit ? "Annual increment" : "Starting package agreed at hiring"}
                  className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
              </Field>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            {isEdit ? "Save changes" : "Add employee"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
